import 'rc-slider/assets/index.css';
import {
  faBracketRoundedSquareLeft, faBracketRoundedSquareRight,
} from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import { css } from '@emotion/react';
import Slider from 'rc-slider';
import { type MarkObj } from 'rc-slider/lib/Marks';
import {
  cloneElement, type CSSProperties, forwardRef, type HTMLAttributes, type ReactElement, type ReactNode, useCallback,
  useMemo,
} from 'react';
import type { Writable } from 'ts-essentials';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';
import { isNumber } from '~/_shared/utils/typeGuards';
import { useTheme } from '../../themes/theme.hooks';
import { type ThemeProps } from '../../types/themeProps';
import { isArray } from '../../utils/collections/collections';
import {
  HANDLE_HOVER_SCALE, RAIL_HOVERED_HANDLE_CLASS,
} from './slider.constants';
import { useRailHoveredHandle } from './useRailHoveredHandle.hook';

export type SliderMarks = Record<string | number, ReactNode | MarkObj>;

export enum SliderConstraintType {
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
}

export type SliderConstraint = {
  type: SliderConstraintType;
  value: number;
};

const DEFAULT_HANDLE_COLOR = '#fff';

const outOfConstraintHandleStyle = ({ theme }: ThemeProps): CSSProperties => ({
  backgroundColor: theme.sliderColors.danger,
  transition: 'background .2s',
});

const handleStyle = ({ theme, index }: ThemeProps<{ index: number }>): CSSProperties => ({
  backgroundColor: index % 2 === 0 ? theme.sliderColors.contrastColor : DEFAULT_HANDLE_COLOR,
});

const constraintMarkIconContainerStyle = ({ isGreaterThan }: { isGreaterThan: boolean }): CSSProperties => ({
  marginLeft: isGreaterThan ? 2 : -2,
});

const constraintMarkIconStyle = ({ theme, index }: ThemeProps<{ index: number }>) => css({
  color: index % 2 === 0 ? theme.sliderColors.contrastColor : DEFAULT_HANDLE_COLOR,
  fontSize: 14,
});

const constraintDotStyle: CSSProperties = {
  display: 'none',
  pointerEvents: 'none',
};

export type SliderValue = readonly [number, ...number[]];

type SliderProps = Readonly<{
  className?: string;
  constraints?: SliderConstraint | SliderConstraint[]; //constraints need to be in the same order as value, take priority over marks and dots
  dotStyle?: CSSProperties | ((value: number) => CSSProperties);
  included?: boolean;
  disabled?: boolean;
  marks?: SliderMarks;
  max: number;
  min: number;
  reverse?: boolean;
  step: number;
  value: SliderValue;
  vertical?: boolean;
  withDots?: boolean;
  children?: ReactNode;
  onChange: (value: SliderValue) => void;
}>;

// `scale` also scales rc-slider's centering translate, so the origin is the handle's center after that translate
const getHandleTransformOrigin = (isVertical?: boolean, isReversed?: boolean): string => {
  if (isVertical) {
    return isReversed ? '50% 0' : '50% 100%';
  }
  return isReversed ? '100% 50%' : '0 50%';
};

const sliderStyle = ({ theme, isVertical, isReversed, disabled, marksOnLine }: ThemeProps<{ isVertical?: boolean; isReversed?: boolean; disabled?: boolean; marksOnLine?: boolean }>) => {
  const widthOrHeightProp = isVertical ? { width: 4 } : { height: 4 };

  return css({
    '.rc-slider-track': {
      background: theme.sliderColors.trackBackground,
      ...widthOrHeightProp,
    },
    '.rc-slider-rail': {
      background: theme.sliderColors.railBackground,
      ...widthOrHeightProp,
    },
    '.rc-slider-dot': {
      backgroundColor: theme.sliderColors.dotColor,
      borderColor: theme.sliderColors.dotBorder,
      cursor: 'default',
    },
    '.rc-slider-dot-active': {
      backgroundColor: theme.sliderColors.activeDotColor,
      borderColor: theme.sliderColors.activeDotBorder,
    },
    '.rc-slider-handle': {
      '&, &.rc-slider-handle-dragging': {
        border: 'none',
        cursor: disabled ? 'default' : 'grab',
        opacity: 1,
        backgroundColor: DEFAULT_HANDLE_COLOR,
        zIndex: 1,
      },
      // Not set while dragging, so rc-slider's dragging glow shows
      '&': {
        boxShadow: `0px 3px 7px ${theme.sliderColors.handleShadow}`,
      },
      transition: 'scale .15s',
      transformOrigin: getHandleTransformOrigin(isVertical, isReversed),
      ...(disabled ? {} : {
        [`&:hover, &.${RAIL_HOVERED_HANDLE_CLASS}`]: {
          scale: `${HANDLE_HOVER_SCALE}`,
        },
        '&.rc-slider-handle-dragging': {
          cursor: 'grabbing',
          scale: '1',
          transition: 'none',
        },
      }),
    },
    '&.rc-slider-disabled': {
      backgroundColor: 'inherit',
    },
    ...(marksOnLine ? {
      '.rc-slider-mark': {
        top: 0,
        pointerEvents: 'none',
      },
    } : {}),

    opacity: disabled ? theme.opacity.disabled : undefined,
  });
};

export const SliderComponent = forwardRef<HTMLDivElement, SliderProps>(({
  children,
  className,
  constraints,
  disabled,
  dotStyle,
  included,
  marks,
  max,
  min,
  onChange,
  reverse,
  step,
  value,
  vertical,
  withDots,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const changeHandler = useThrottle((values: number | readonly number[]) => {
    if (isArray(values)) {
      if (notEmpty(values)) {
        onChange(values);
      }
      else {
        console.error('Invariant exception: slider value cannot be empty');
        return;
      }
    }
    else {
      onChange([values]);
    }
  }, [onChange], 10);

  const constraintsMarksAndStyles = useMemo(() => {
    if (!constraints) {
      return null;
    }

    const values = value;

    return (isArray(constraints) ? constraints : [constraints]).reduce((acc, constraint, index) => {
      acc.marks[acc.marks[constraint.value] ? constraint.value + 0.0001 : constraint.value] = {
        label: (
          <FontAwesomeIcon
            key={constraint.type === SliderConstraintType.GreaterThan
              ? 'dash-left' : 'dash-right'}
            icon={constraint.type === SliderConstraintType.GreaterThan
              ? faBracketRoundedSquareLeft : faBracketRoundedSquareRight}
            css={constraintMarkIconStyle({ theme, index })}
          />
        ),
        style: constraintMarkIconContainerStyle({ isGreaterThan: constraint.type === SliderConstraintType.GreaterThan }),
      };
      acc.handleStyle.push(
        (
          (values[index] && constraint.type === SliderConstraintType.GreaterThan && (values[index] ?? 0) < constraint.value)
          || (values[index] && constraint.type === SliderConstraintType.LessThan && (values[index] ?? 0) > constraint.value)
        ) ? outOfConstraintHandleStyle({ theme }) : handleStyle({ theme, index })
      );
      return acc;
    }, { handleStyle: [] as CSSProperties[], marks: {} as SliderMarks });
  }, [constraints, value, theme]);

  const [singleValue] = value;

  const railHoverCandidates = useMemo(() =>
    value.map((handleValue: number, index: number) => ({ index, value: handleValue })),
  [value]);

  const { hoveredIndex, onMouseMove, onMouseLeave, classNames } = useRailHoveredHandle({
    candidates: railHoverCandidates,
    min,
    max,
    enabled: !disabled,
    vertical,
    reverse,
  });

  const handleRender = useCallback((
    node: ReactElement<HTMLAttributes<HTMLDivElement>>,
    { index }: { index: number },
  ): ReactElement => (index === hoveredIndex
    ? cloneElement(node, { className: `${node.props.className ?? ''} ${RAIL_HOVERED_HANDLE_CLASS}` })
    : node), [hoveredIndex]);

  return (
    <div
      {...restProps}
      ref={ref}
      className={className}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    >
      {value.length === 1 && isNumber(singleValue) && (
        <Slider
          range={false}
          classNames={classNames}
          css={sliderStyle({
            theme,
            isVertical: vertical,
            isReversed: reverse,
            disabled,
            marksOnLine: !!constraintsMarksAndStyles,
          })}
          disabled={disabled}
          dotStyle={constraintsMarksAndStyles?.marks ? constraintDotStyle : dotStyle}
          dots={withDots}
          included={included}
          marks={constraintsMarksAndStyles?.marks || marks}
          max={max}
          min={min}
          onChange={changeHandler}
          reverse={reverse}
          step={step}
          value={singleValue}
          vertical={vertical}
          handleStyle={constraintsMarksAndStyles?.handleStyle[0] || undefined}
          handleRender={handleRender}
        />
      )}

      {value.length > 1 && (
        <Slider
          classNames={classNames}
          range
          allowCross={false}
          css={sliderStyle({
            theme,
            isVertical: vertical,
            isReversed: reverse,
            disabled,
            marksOnLine: !!constraintsMarksAndStyles,
          })}
          disabled={disabled}
          dotStyle={constraintsMarksAndStyles?.marks ? constraintDotStyle : dotStyle}
          marks={constraintsMarksAndStyles?.marks || marks}
          max={max}
          min={min}
          onChange={changeHandler}
          reverse={reverse}
          step={step}
          vertical={vertical}
          value={value as Writable<SliderValue>}
          handleStyle={constraintsMarksAndStyles?.handleStyle.length
            ? constraintsMarksAndStyles?.handleStyle : undefined
          }
          handleRender={handleRender}
        />
      )}

      {children}
    </div>
  );
});
